import React, { ComponentProps, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dimensions, Platform, StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { SelectMembersList } from '@components/Invite/SelectMembersList';
import { Box } from '@components/Restyle';
import ActivityIndicatorLoading from '@components/shared/ActivityIndicatorLoading';
import { ButtonNext } from '@components/shared/Button/ButtonNext';
import Icon from '@components/shared/Icon/Icon';
import Overlay from '@components/Tutorialization/Overlay';
import TooltipPopup from '@components/Tutorialization/TooltipPopup';
import { Chat, useGetChatQuery, useListGroupsQuery } from '@graphql/generated';
import { useContacts } from '@hooks/useContacts';
import { useListTeamsFromQuery } from '@hooks/useListTeamsFromQuery';
import { useProjectTutorial } from '@hooks/useProjectTutorial';
import theme from '@themes/theme';
import { heightPixel } from '@utils/normalise';
import { AsyncStorage } from '@utils/storage';

type AddMembersProps = {
  isCreateTask?: boolean;
  projectUUId?: string;
  projectId?: string;
  isCreateProject?: boolean;
  teamId?: string;
  teamUUId?: string;
  disabled?: boolean;
  loading?: boolean;
  isEdit?: boolean;
  changesMade?: boolean;
  isFromEditProject?: boolean;
  showTaskTagSupportUser?: boolean;
  onNextPress?: () => void;
  chatId?: Chat['id'];
  isFromProjectOnly?: boolean;
  isFromGroupChat?: boolean;
} & Omit<ComponentProps<typeof SelectMembersList>, 'contacts'>;

export const AddMembers = ({
  isCreateTask = false,
  isCreateProject = false,
  showTaskTagSupportUser = true,
  projectUUId = '',
  teamUUId = '',
  disabled,
  isEdit,
  changesMade,
  loading,
  onNextPress,
  chatId = '',
  isFromProjectOnly = false,
  isFromGroupChat = false,
  ...selectMembersListProps
}: AddMembersProps) => {
  const TASKTAG_SUPPORT_EMAIL = 'emmanueln@lovettcommercial.com';
  const { t } = useTranslation();
  const insets = useSafeAreaInsets();
  const {
    isVisible,
    addMembersTutorialState,
    setAddMemeberTutorialStateAndInStorage,
    setHeaderOverlayActive,
    headerOverlayActive,
  } = useProjectTutorial();

  const { data } = useGetChatQuery({
    variables: { id: chatId },
    skip: !chatId,
  });
  const { loading: isLoading } = useListTeamsFromQuery();

  const { getChat: chat } = data || { chat: undefined };

  const { contacts } = useContacts();

  useEffect(() => {
    const checkIfAddMemberTutorialFlowIsCompleted = async () => {
      const tutorial_add_member = await AsyncStorage.getItem(
        'tutorial_add_member'
      );

      if (
        !tutorial_add_member &&
        !isLoading &&
        isVisible &&
        (addMembersTutorialState === '1' || addMembersTutorialState === '2')
      ) {
        setTimeout(() => {
          setHeaderOverlayActive(true);
        }, 500);
      }
    };
    checkIfAddMemberTutorialFlowIsCompleted();
    return () => setHeaderOverlayActive(false);
  }, [isLoading, isVisible, addMembersTutorialState]);

  const _filteredContacts =
    chatId != ''
      ? contacts.filter((c) => chat!.users.some((u) => u.id == c.id))
      : contacts;

  const { data: listGroup } = useListGroupsQuery();
  const listGroupData = listGroup?.listGroups;
  const height = Dimensions.get('window').height;

  return (
    <Box flex={1} accessibilityLabel='Add Members' backgroundColor='white'>
      <Box flex={1} style={{ marginBottom: insets.bottom }}>
        <SelectMembersList
          showTaskTagSupportUser={showTaskTagSupportUser}
          isCreateTask={isCreateTask}
          isCreateProject={isCreateProject}
          teamUUId={teamUUId}
          isFromProjectOnly={isFromProjectOnly}
          projectUUId={projectUUId}
          listGroup={listGroupData}
          contacts={
            showTaskTagSupportUser
              ? _filteredContacts
              : _filteredContacts.filter(
                  (c) => c.email !== TASKTAG_SUPPORT_EMAIL
                )
          }
          {...selectMembersListProps}
          showTaskTagUsers
          hideTeamTab={false}
          isFromEditProject={true}
          isFromGroupChat={isFromGroupChat}
        />

        {!isLoading && (
          <Overlay
            backgroundColor='rgba(0, 0, 0, 0.5)'
            visible={
              !isLoading &&
              headerOverlayActive &&
              isVisible &&
              addMembersTutorialState === '1'
            }
            children={
              <Box
                position='absolute'
                top={
                  Platform.OS === 'ios'
                    ? height * 0.05 + 100
                    : height * 0.05 + 120
                }
                left='5%'
                pointerEvents='box-none'>
                <TooltipPopup
                  animate
                  visible={
                    !isLoading && isVisible && addMembersTutorialState === '1'
                  }
                  title='Select Member(s)'
                  description='Feel free to add/search as many Tasktag members as you like to the project.'
                  pointerPosition='top-left'
                  primaryBtn={async () => {
                    setAddMemeberTutorialStateAndInStorage('2');
                  }}
                  primaryBtnTitle='Next'
                />
              </Box>
            }
          />
        )}
      </Box>
      <Overlay
        backgroundColor='rgba(0, 0, 0, 0.5)'
        visible={
          isVisible && headerOverlayActive && addMembersTutorialState === '2'
        }
        children={
          <Box
            position='absolute'
            bottom={
              Platform.OS === 'ios'
                ? height * 0.05 + heightPixel(160)
                : height * 0.05 + heightPixel(180)
            }
            left='15%'
            pointerEvents='box-none'>
            <TooltipPopup
              animate
              visible={
                !isLoading &&
                isVisible &&
                headerOverlayActive &&
                addMembersTutorialState === '2'
              }
              title='Confirm Members'
              description='After selecting your members, just hit this button to confirm your selection.'
              pointerPosition='bottom-right'
              primaryBtn={() => setAddMemeberTutorialStateAndInStorage('3')}
              primaryBtnTitle='Got it'
            />
          </Box>
        }
      />
      <Box
        style={[
          styles.bottomButtonWeb,
          {
            paddingBottom: insets.bottom > 0 ? insets.bottom : undefined,
          },
        ]}>
        <ButtonNext
          suffix={
            <Icon
              variant='l'
              name='ArrowRight'
              color={disabled ? 'grey04' : 'white'}
            />
          }
          onPress={onNextPress}
          disabled={isEdit ? disabled || !changesMade : disabled}
          variant='primary'
          borderRadius='xl'
          paddingHorizontal='l'
          accessibilityLabel={t(
            isCreateProject || isCreateTask ? 'shared:done' : 'shared:next'
          )}>
          {loading ? <ActivityIndicatorLoading /> : t('shared:next')}
        </ButtonNext>
      </Box>
    </Box>
  );
};

const styles = StyleSheet.create({
  bottomButton: {
    position: 'absolute',
    bottom: theme.spacing.l,
    right: theme.spacing.l,
  },
  bottomButtonWeb: {
    position: 'absolute',
    bottom: 0,
    right: theme.spacing.l,
  },
});
